import { supabase } from './supabase';
import { Tables, TablesInsert, TablesUpdate } from '../types/database.types';
import { logger } from '../utils/logger';

export type Menu = Tables<'menus'>;
export type MenuInsert = TablesInsert<'menus'>;
export type MenuUpdate = TablesUpdate<'menus'>;
export type MenuPermission = Tables<'menu_permissions'>;
export type MenuPermissionInsert = TablesInsert<'menu_permissions'>;
export type MenuPermissionUpdate = TablesUpdate<'menu_permissions'>;

export type RoleType = 'super_admin' | 'admin' | 'user';

export interface MenuWithPermissions extends Menu {
  permissions?: MenuPermission[];
  children?: MenuWithPermissions[];
}

class MenuService {
  // 메뉴 목록 조회
  async getMenus(includeInactive = false): Promise<MenuWithPermissions[]> {
    try {
      let query = supabase.from('menus').select(`
        *,
        permissions:menu_permissions(*)
      `);

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      query = query.order('sort_order', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      // 트리 구조로 변환
      return this.buildMenuTree(data || []);
    } catch (error) {
      logger.error('Error fetching menus', error);
      throw error;
    }
  }

  // 메뉴 트리 구조 생성
  private buildMenuTree(menus: MenuWithPermissions[]): MenuWithPermissions[] {
    const menuMap = new Map<string, MenuWithPermissions>();
    const rootMenus: MenuWithPermissions[] = [];

    // 먼저 모든 메뉴를 맵에 저장
    menus.forEach(menu => {
      menuMap.set(menu.id, { ...menu, children: [] });
    });

    // 부모-자식 관계 설정
    menus.forEach(menu => {
      if (menu.parent_id) {
        const parent = menuMap.get(menu.parent_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(menuMap.get(menu.id)!);
        }
      } else {
        rootMenus.push(menuMap.get(menu.id)!);
      }
    });

    return rootMenus;
  }

  // 사용자 권한에 따른 메뉴 조회
  async getUserMenus(userId: string): Promise<MenuWithPermissions[]> {
    try {
      // 사용자 정보 조회
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('is_admin, has_admin_role')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // 역할 결정
      let roleType: RoleType = 'user';
      if (userData?.is_admin) {
        roleType = 'super_admin';
      } else if (userData?.has_admin_role) {
        roleType = 'admin';
      }

      // 메뉴와 권한 조회
      const { data: menus, error: menuError } = await supabase
        .from('menus')
        .select(`
          *,
          permissions:menu_permissions!inner(*)
        `)
        .eq('is_active', true)
        .eq('permissions.role', roleType)
        .eq('permissions.can_view', true)
        .order('sort_order', { ascending: true });

      if (menuError) throw menuError;

      return this.buildMenuTree(menus || []);
    } catch (error) {
      logger.error('Error fetching user menus', error, { userId });
      throw error;
    }
  }

  // 메뉴 생성
  async createMenu(menu: MenuInsert): Promise<Menu> {
    try {
      const { data, error } = await supabase
        .from('menus')
        .insert(menu)
        .select()
        .single();

      if (error) throw error;

      // 기본 권한 설정 (슈퍼관리자만 모든 권한)
      await this.createDefaultPermissions(data.id);

      return data;
    } catch (error) {
      logger.error('Error creating menu', error);
      throw error;
    }
  }

  // 기본 권한 생성
  private async createDefaultPermissions(menuId: string) {
    const permissions: MenuPermissionInsert[] = [
      {
        menu_id: menuId,
        role: 'super_admin',
        can_view: true,
        can_create: true,
        can_update: true,
        can_delete: true,
      },
      {
        menu_id: menuId,
        role: 'admin',
        can_view: false,
        can_create: false,
        can_update: false,
        can_delete: false,
      },
      {
        menu_id: menuId,
        role: 'user',
        can_view: false,
        can_create: false,
        can_update: false,
        can_delete: false,
      },
    ];

    const { error } = await supabase.from('menu_permissions').insert(permissions);
    if (error) logger.error('Error creating default permissions', error, { menuId });
  }

  // 메뉴 수정
  async updateMenu(id: string, menu: MenuUpdate): Promise<Menu> {
    try {
      const { data, error } = await supabase
        .from('menus')
        .update(menu)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error updating menu', error, { id });
      throw error;
    }
  }

  // 메뉴 삭제
  async deleteMenu(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('menus')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      logger.error('Error deleting menu', error, { id });
      throw error;
    }
  }

  // 메뉴 순서 변경
  async updateMenuOrder(menus: { id: string; sort_order: number }[]): Promise<void> {
    try {
      const promises = menus.map(menu =>
        supabase
          .from('menus')
          .update({ sort_order: menu.sort_order })
          .eq('id', menu.id)
      );

      await Promise.all(promises);
    } catch (error) {
      logger.error('Error updating menu order', error);
      throw error;
    }
  }

  // 권한 조회
  async getPermissions(menuId?: string): Promise<MenuPermission[]> {
    try {
      let query = supabase.from('menu_permissions').select('*');

      if (menuId) {
        query = query.eq('menu_id', menuId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error fetching permissions', error, { menuId });
      throw error;
    }
  }

  // 권한 수정
  async updatePermission(id: string, permission: MenuPermissionUpdate): Promise<MenuPermission> {
    try {
      const { data, error } = await supabase
        .from('menu_permissions')
        .update(permission)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error updating permission', error, { id });
      throw error;
    }
  }

  // 역할별 권한 일괄 수정
  async updateRolePermissions(
    roleType: RoleType,
    permissions: { menu_id: string; can_view: boolean; can_create?: boolean; can_update?: boolean; can_delete?: boolean }[]
  ): Promise<void> {
    try {
      const promises = permissions.map(perm =>
        supabase
          .from('menu_permissions')
          .update({
            can_view: perm.can_view,
            can_create: perm.can_create || false,
            can_update: perm.can_update || false,
            can_delete: perm.can_delete || false,
          })
          .eq('menu_id', perm.menu_id)
          .eq('role', roleType)
      );

      await Promise.all(promises);
    } catch (error) {
      logger.error('Error updating role permissions', error, { roleType });
      throw error;
    }
  }

  // 메뉴 접근 권한 확인
  async checkMenuAccess(userId: string, menuPath: string, action: 'view' | 'create' | 'update' | 'delete' = 'view'): Promise<boolean> {
    try {
      // 사용자 정보 조회
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('is_admin, has_admin_role')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // 슈퍼관리자는 모든 접근 허용
      if (userData?.is_admin) return true;

      // 역할 결정
      const roleType: RoleType = userData?.has_admin_role ? 'admin' : 'user';

      // 메뉴 권한 확인
      const { data: permission, error: permError } = await supabase
        .from('menu_permissions')
        .select('*')
        .eq('role', roleType)
        .eq('menus.path', menuPath)
        .single();

      if (permError) return false;

      // 액션별 권한 확인
      switch (action) {
        case 'view':
          return permission?.can_view || false;
        case 'create':
          return permission?.can_create || false;
        case 'update':
          return permission?.can_update || false;
        case 'delete':
          return permission?.can_delete || false;
        default:
          return false;
      }
    } catch (error) {
      logger.error('Error checking menu access', error, { userId, menuPath, action });
      return false;
    }
  }
}

export const menuService = new MenuService();